/**
 * AP-37 Detailblatt O 5. — **Reiter Material** des Cockpits „Materialanfragen":
 * alle Kostenzeilen der laufenden Kalkulationen, gruppiert nach Lieferant,
 * Kalkulation oder Material, mit Anfragestatus. Listen-Standard (FilterBar,
 * Spalten, Sortierung, Summe, Export mit Mail); die Gruppierung gehört zum
 * Spalten-Layout und wird mit ihm gemerkt.
 *
 * Gefiltert und sortiert wird auf dem Server; eine Liste mit Gruppen läuft
 * nicht über Seiten (O-27).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, ArrowUpDown, Ban, Check, ChevronDown, ChevronRight, Copy, Paperclip, RotateCcw, Send, Settings } from 'lucide-react'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { DirektLieferant, DirektPreis } from '@/components/list/DirektFeld'
import { FilterBar } from '@/components/list/FilterBar'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { ExportButton } from '@/components/ui/ExportButton'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { api } from '@/lib/axios'
import { mitStand, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { fetchMaterial, materialExportRequest, materialKopieren, materialUeberspringen } from '../../api/calculation-requests.api'
import { costLineUrl, searchSuppliers } from '../../api/calculation-sheet.api'
import { fetchCalcBomCategories } from '../../api/calculation-settings.api'
import { lookupUsers } from '../../api/calculations.api'
import { datumText, tagText, abgabeDringend } from '../../lib/kalkulation-labels'
import type { CalcLookupUser } from '../../types/calculation.types'
import type { AnfrageZustand, MaterialListParams, MaterialListResponse, MaterialRow } from '../../types/calculation-requests.types'

export const ZUSTAND_ANZEIGE: Record<AnfrageZustand, { label: string; klasse: string }> = {
  OPEN: { label: 'offen', klasse: 'bg-muted text-muted-foreground' },
  SKIPPED: { label: 'nicht anfragen', klasse: 'bg-muted text-muted-foreground line-through' },
  DRAFT: { label: 'im Entwurf', klasse: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
  REQUESTED: { label: 'angefragt', klasse: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200' },
  ANSWERED: { label: 'Angebot da', klasse: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
  APPLIED: { label: 'übernommen', klasse: 'bg-primary/15 text-primary' },
}
const PREISQUELLE: Record<string, string> = { ARTICLE: 'Artikel', MANUAL: 'von Hand', OFFER: 'Angebot' }
const KOSTENART: Record<string, string> = { MATERIAL: 'Material', SUBCONTRACT: 'Fremdleistung', EQUIPMENT: 'Gerät' }

type Gruppierung = 'supplier' | 'calculation' | 'material' | 'none'

/**
 * Nachtrag 9 (Auftraggeber 13.10.2026): **Einkaufspreis und Lieferant direkt in der Liste ändern.** Dieselbe
 * Speicherstelle wie die Kostenzeile im Kalkulationsblatt (`calcCostLine:<id>`), derselbe PATCH — der Server
 * rechnet die Position neu und merkt den Preis als „von Hand". Die Liste lädt danach neu: Gesamtpreis, Preisstand
 * und die Summe kommen von dort.
 */
function useKostenzeilenStelle(z: MaterialRow, onGeaendert: () => void): Speicherstelle<unknown> {
  const url = costLineUrl(z.calculationId, z.positionId, z.costLineId)
  // Stabil, solange die Zeile sich nicht ändert — die Liste baut ihre Zeilen bei jedem Zeichnen neu.
  const daten = useMemo<Datensatz>(
    () => ({ unitCost: z.unitCost ?? null, supplierId: z.supplierId, updatedAt: z.updatedAt }),
    [z.unitCost, z.supplierId, z.updatedAt],
  )
  return useSpeicherstelle<Datensatz>({
    schluessel: `calcCostLine:${z.costLineId}`,
    bezeichnung: z.name,
    daten,
    schreibe: async (patch, stand) => {
      const antwort = await api.patch<{ data: Datensatz }>(url, patch, mitStand(stand))
      onGeaendert()
      return antwort.data.data
    },
    keepalive: { url, method: 'PATCH' },
  }) as Speicherstelle<unknown>
}

function EkZelle({ z, onGeaendert }: { z: MaterialRow; onGeaendert: () => void }) {
  const stelle = useKostenzeilenStelle(z, onGeaendert)
  return (
    <>
      <DirektPreis stelle={stelle} feld="unitCost" label="Einkaufspreis" />
      {z.priceUnit > 1 ? ` / ${z.priceUnit}` : ''}
      {z.discountPercent ? <span className="text-xs text-muted-foreground"> −{z.discountPercent} %</span> : null}
    </>
  )
}

function LieferantZelle({ z, onGeaendert }: { z: MaterialRow; onGeaendert: () => void }) {
  const stelle = useKostenzeilenStelle(z, onGeaendert)
  return (
    <>
      <DirektLieferant stelle={stelle} name={z.supplierName} suche={searchSuppliers} />
      {z.supplierName && !z.supplierIsSupplier && <span className="block px-1.5 text-[11px] text-destructive">nicht als Lieferant angelegt — wird nicht angefragt</span>}
    </>
  )
}

interface ColDef {
  key: string
  label: string
  align?: 'right'
  kosten?: boolean
}

const ALLE_SPALTEN: ColDef[] = [
  { key: 'calculation', label: 'Kalkulation' },
  { key: 'dueAt', label: 'Abgabe' },
  { key: 'position', label: 'Position' },
  { key: 'quantity', label: 'Menge', align: 'right' },
  { key: 'unit', label: 'Einheit' },
  { key: 'name', label: 'Bezeichnung' },
  { key: 'articleNumber', label: 'Artikel-Nr.' },
  { key: 'supplier', label: 'Lieferant' },
  { key: 'priceState', label: 'Preisstand' },
  { key: 'unitCost', label: 'Einkaufspreis', align: 'right', kosten: true },
  { key: 'cost', label: 'Gesamtpreis', align: 'right', kosten: true },
  { key: 'requestState', label: 'Anfrage' },
  { key: 'files', label: 'Dateien', align: 'right' },
  { key: 'owner', label: 'Kalkulant' },
  { key: 'weight', label: 'Gewicht', align: 'right' },
  { key: 'kind', label: 'Kostenart' },
  // Nachtrag 7 (N7-7): einblendbar, nicht in der Vorgabe.
  { key: 'category', label: 'Kategorie' },
]
// Vorgabe des Auftraggebers (27.9.2026): Menge, Einheit (darunter die Alternativmenge), Bezeichnung, Lieferant,
// Preisstand, Einkaufspreis, Gesamtpreis, Anfrage, Dateien, Kalkulation — ohne Gruppierung. Neuer Schlüssel, damit die
// neue Vorgabe auch bei denen ankommt, die die Liste schon einmal eingerichtet hatten.
const DEFAULT_KEYS = ['quantity', 'unit', 'name', 'supplier', 'priceState', 'unitCost', 'cost', 'requestState', 'files', 'calculation']
const LAYOUT_KEY = 'columnbar_layout_calculation_material_v3'

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
  sortKey: string
  sortDir: 'asc' | 'desc'
  grouping: Gruppierung
}
const VORGABE: ColumnState = { columnKeys: DEFAULT_KEYS, columnWidths: {}, sortKey: '', sortDir: 'asc', grouping: 'none' }

const DEFAULT_FILTER_KEYS = ['search', 'requestState', 'supplier', 'calculation']
const DEFAULT_FILTER_VALUES: Record<string, ActiveFilter> = {
  requestState: { fieldKey: 'requestState', operator: 'is', value: 'OPEN,DRAFT,REQUESTED,ANSWERED' },
}

const mengeText = (x: number) => x.toLocaleString('de-DE', { maximumFractionDigits: 3 })
const kalkText = (z: MaterialRow) => `${z.calculationNumber}${z.calculationRevision > 1 ? ` · F${z.calculationRevision}` : ''}`

function filterParams(filters: Record<string, ActiveFilter>, vorgabe: { calculationId?: string }): MaterialListParams {
  const p: MaterialListParams = {}
  if (vorgabe.calculationId) p.calculationIds = vorgabe.calculationId
  for (const [key, a] of Object.entries(filters)) {
    if (!a.value) continue
    if (key === 'search') p.search = a.value
    if (key === 'requestState') p.requestState = a.value
    if (key === 'calculation') p.calculationIds = a.value
    if (key === 'owner') p.ownerUserId = a.value
    if (key === 'dueTo') p.dueTo = a.value.slice(0, 10)
    if (key === 'supplier') p.supplierIds = a.value
    if (key === 'category') p.categoryIds = a.value
    if (key === 'priceState') p.priceState = a.value
    if (key === 'kinds') p.kinds = a.value
    if (key === 'calculationStatus') p.calculationStatus = a.value
    if (key === 'withoutOptional') p.withoutOptional = a.value === 'true'
    if (key === 'withQuick') p.withQuick = a.value === 'true'
    if (key === 'withFiles') p.withFiles = a.value === 'true'
  }
  return p
}

interface Gruppe {
  key: string
  label: string
  sub?: string
  rows: MaterialRow[]
}

function gruppiere(rows: MaterialRow[], art: Gruppierung): Gruppe[] {
  // „ohne Gruppierung": eine Gruppe ohne Kopfzeile, Reihenfolge wie vom Server sortiert.
  if (art === 'none') return rows.length ? [{ key: '__alle__', label: '', rows }] : []
  const map = new Map<string, Gruppe>()
  for (const z of rows) {
    const key = art === 'supplier' ? (z.supplierId ?? '') : art === 'calculation' ? z.calculationId : z.materialKey
    const g =
      map.get(key) ??
      (art === 'supplier'
        ? { key, label: z.supplierName ?? 'ohne Lieferant', rows: [] }
        : art === 'calculation'
          ? { key, label: `${kalkText(z)} ${z.calculationName}`, sub: z.dueAt ? `Abgabe ${datumText(z.dueAt, true)}` : undefined, rows: [] }
          : { key, label: z.name, sub: z.unitCode ?? undefined, rows: [] })
    g.rows.push(z)
    map.set(key, g)
  }
  const gruppen = [...map.values()]
  if (art === 'supplier') {
    // „ohne Lieferant" zuerst (5.2), dann nach Name.
    gruppen.sort((a, b) => (a.key === '' ? -1 : b.key === '' ? 1 : a.label.localeCompare(b.label, 'de')))
  } else if (art === 'calculation') {
    gruppen.sort((a, b) => (a.rows[0].dueAt ?? '9999').localeCompare(b.rows[0].dueAt ?? '9999'))
  } else {
    gruppen.sort((a, b) => a.label.localeCompare(b.label, 'de'))
  }
  return gruppen
}

export function MaterialReiter({
  calculationId,
  onZaehler,
  onAnfragen,
  neuLaden,
  alleMarkieren,
}: {
  /** Vorfilter aus der Adresse (`?calculationId=`), z. B. aus dem Materialauszug */
  calculationId?: string
  onZaehler?: (z: MaterialListResponse['counters']) => void
  /** O2: Preisanfrage erzeugen — die gewählten Zeilen */
  onAnfragen?: (rows: MaterialRow[]) => void
  /** von außen erhöht, wenn neu geladen werden soll */
  neuLaden?: number
  /** 10.: aus dem Materialauszug — nach dem ersten Laden alle Zeilen markieren */
  alleMarkieren?: boolean
}) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const kosten = erfuelltRecht('calculation.cost.view', user?.permissions)
  // Wie im Kalkulationsblatt: Kostenzeilen ändert, wer Kalkulationen bearbeiten und Kosten sehen darf.
  const darfAendern = kosten && erfuelltRecht('calculation.calculation.manage', user?.permissions)
  const alleSpalten = useMemo(() => ALLE_SPALTEN.filter((c) => !c.kosten || kosten), [kosten])

  const [users, setUsers] = useState<CalcLookupUser[]>([])
  useEffect(() => {
    lookupUsers().then(setUsers).catch(() => setUsers([]))
  }, [])
  // N7-7: die Kategorien des Mandanten — alle, nicht nur die der gerade gelisteten Zeilen.
  const [kategorien, setKategorien] = useState<{ value: string; label: string }[]>([])
  useEffect(() => {
    fetchCalcBomCategories()
      .then((liste) => setKategorien(liste.map((k) => ({ value: k.id, label: k.name }))))
      .catch(() => setKategorien([]))
  }, [])
  // Auswahl für Lieferant und Kalkulation aus dem, was schon geladen wurde (wächst mit jeder Antwort).
  const optionen = useRef({ lieferanten: new Map<string, string>(), kalkulationen: new Map<string, string>() })
  const [optionenStand, setOptionenStand] = useState(0)

  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [colState, setColState] = useState<ColumnState>(() => ({ ...VORGABE, ...loadUiSetting<Partial<ColumnState> | null>(LAYOUT_KEY, null) }))
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [result, setResult] = useState<MaterialListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set())
  const [zu, setZu] = useState<Set<string>>(new Set())
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const markieren = useRef(Boolean(alleMarkieren))
  const dragKeyRef = useRef<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  const fetchData = useCallback(
    async (filters: Record<string, ActiveFilter>, state: ColumnState) => {
      setLoading(true)
      setError(null)
      try {
        const r = await fetchMaterial({ ...filterParams(filters, { calculationId }), ...(state.sortKey ? { sortBy: state.sortKey, sortDir: state.sortDir } : {}) })
        for (const z of r.data) {
          optionen.current.lieferanten.set(z.supplierId ?? 'none', z.supplierName ?? 'ohne Lieferant')
          optionen.current.kalkulationen.set(z.calculationId, `${kalkText(z)} ${z.calculationName}`)
        }
        setOptionenStand((x) => x + 1)
        setResult(r)
        onZaehler?.(r.counters)
        // Eine Auswahl, deren Zeile nicht mehr gelistet ist, fällt weg.
        if (markieren.current) {
          markieren.current = false
          setAuswahl(new Set(r.data.map((z) => z.costLineId)))
        } else setAuswahl((alt) => new Set([...alt].filter((id) => r.data.some((z) => z.costLineId === id))))
      } catch (e) {
        setError(apiErrorText(e, 'Das Material ließ sich nicht laden.'))
      } finally {
        setLoading(false)
      }
    },
    [calculationId, onZaehler],
  )

  useEffect(() => {
    // Nach einer Aktion von außen (Preisanfragen angelegt) beginnt die Auswahl neu.
    if (!markieren.current) setAuswahl(new Set())
    void fetchData(filterValues, colState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neuLaden, calculationId])

  const filterFields = useMemo<FilterFieldDef[]>(
    () => [
      { key: 'search', label: 'Suche', type: 'text', category: 'core', operators: ['contains'] },
      {
        key: 'requestState',
        label: 'Anfrage',
        type: 'select-multi',
        category: 'core',
        operators: ['is'],
        options: (Object.keys(ZUSTAND_ANZEIGE) as AnfrageZustand[]).map((s) => ({ value: s, label: ZUSTAND_ANZEIGE[s].label })),
      },
      {
        key: 'supplier',
        label: 'Lieferant',
        type: 'select-multi',
        category: 'core',
        operators: ['is'],
        options: [...optionen.current.lieferanten.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'de')),
      },
      {
        key: 'category',
        label: 'Kategorie',
        type: 'select-multi',
        category: 'core',
        operators: ['is'],
        options: [{ value: 'none', label: 'ohne Kategorie' }, ...kategorien],
      },
      {
        key: 'calculation',
        label: 'Kalkulation',
        type: 'select-multi',
        category: 'core',
        operators: ['is'],
        options: [...optionen.current.kalkulationen.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'de')),
      },
      { key: 'owner', label: 'Kalkulant', type: 'select', category: 'core', operators: ['is'], options: users.map((u) => ({ value: u.id, label: u.name })) },
      { key: 'dueTo', label: 'Abgabe bis', type: 'date', category: 'core', operators: ['is'] },
      {
        key: 'priceState',
        label: 'Preisstand',
        type: 'select-multi',
        category: 'core',
        operators: ['is'],
        options: [
          { value: 'NONE', label: 'ohne Preis' },
          { value: 'OLD', label: `älter als ${result?.priceAgeDays ?? 90} Tage` },
          { value: 'ARTICLE', label: 'Artikel' },
          { value: 'MANUAL', label: 'von Hand' },
          { value: 'OFFER', label: 'aus Angebot' },
        ],
      },
      {
        key: 'kinds',
        label: 'Kostenart',
        type: 'select-multi',
        category: 'core',
        operators: ['is'],
        options: Object.entries(KOSTENART).map(([value, label]) => ({ value, label })),
      },
      {
        key: 'calculationStatus',
        label: 'Status der Kalkulation',
        type: 'select-multi',
        category: 'core',
        operators: ['is'],
        options: [
          { value: 'IN_PROGRESS', label: 'in Arbeit' },
          { value: 'SUBMITTED', label: 'abgegeben' },
        ],
      },
      { key: 'withoutOptional', label: 'ohne Eventual- und Wahlpositionen', type: 'boolean', category: 'core', operators: ['is'] },
      { key: 'withQuick', label: 'mit Pauschalen aus der Schnellerfassung', type: 'boolean', category: 'core', operators: ['is'] },
      { key: 'withFiles', label: 'nur mit Dateien', type: 'boolean', category: 'core', operators: ['is'] },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users, kategorien, optionenStand, result?.priceAgeDays],
  )

  const handleFiltersChange = useCallback(
    (neu: Record<string, ActiveFilter>) => {
      setFilterValues(neu)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => void fetchData(neu, colState), 400)
    },
    [fetchData, colState],
  )

  const updateColState = useCallback((updater: (prev: ColumnState) => ColumnState) => {
    setColState((prev) => {
      const next = updater(prev)
      saveUiSetting(LAYOUT_KEY, next)
      return next
    })
  }, [])

  const handleSort = (key: string) => {
    const dir: 'asc' | 'desc' = colState.sortKey === key && colState.sortDir === 'asc' ? 'desc' : 'asc'
    const next = { ...colState, sortKey: key, sortDir: dir }
    updateColState(() => next)
    void fetchData(filterValues, next)
  }

  const handleDrop = (ziel: string) => {
    const quelle = dragKeyRef.current
    setDragOverKey(null)
    if (!quelle || quelle === ziel) return
    updateColState((prev) => {
      const keys = [...prev.columnKeys]
      const von = keys.indexOf(quelle)
      const nach = keys.indexOf(ziel)
      if (von < 0 || nach < 0) return prev
      keys.splice(von, 1)
      keys.splice(nach, 0, quelle)
      return { ...prev, columnKeys: keys }
    })
  }

  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    const th = (e.target as HTMLElement).closest('th')
    const startW = th?.getBoundingClientRect().width ?? 120
    const startX = e.clientX
    const onMove = (ev: MouseEvent) => {
      const w = Math.max(50, startW + ev.clientX - startX)
      updateColState((prev) => ({ ...prev, columnWidths: { ...prev.columnWidths, [key]: w } }))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const rows = useMemo(() => result?.data ?? [], [result])
  const gruppen = useMemo(() => gruppiere(rows, colState.grouping), [rows, colState.grouping])
  const shownColumns = colState.columnKeys.map((k) => alleSpalten.find((c) => c.key === k)).filter((c): c is ColDef => !!c)
  const availableToAdd = alleSpalten.filter((c) => !colState.columnKeys.includes(c.key))
  const totalCols = 1 + shownColumns.length + (columnConfigMode && availableToAdd.length ? 1 : 0) + 1
  const gewaehlt = rows.filter((z) => auswahl.has(z.costLineId))

  const schalte = (ids: string[], an: boolean) =>
    setAuswahl((alt) => {
      const neu = new Set(alt)
      for (const id of ids) {
        if (an) neu.add(id)
        else neu.delete(id)
      }
      return neu
    })

  const aktion = async (was: 'skip' | 'unskip' | 'copy') => {
    setHinweis(null)
    setError(null)
    const ids = gewaehlt.map((z) => z.costLineId)
    try {
      if (was === 'copy') {
        const r = await materialKopieren(ids)
        setHinweis(`${r.copied} Zeile${r.copied === 1 ? '' : 'n'} in der Ablage — im Belegwesen mit „Einfügen" in eine Preisanfrage übernehmen; mit „Speichern" gelten sie dort als angefragt.`)
        return
      }
      await materialUeberspringen(ids, was === 'skip')
      setAuswahl(new Set())
      await fetchData(filterValues, colState)
    } catch (e) {
      setError(apiErrorText(e, 'Die Aktion ließ sich nicht ausführen.'))
    }
  }

  const pfeil = (key: string) =>
    colState.sortKey === key ? (
      colState.sortDir === 'asc' ? (
        <ArrowUp className="h-3 w-3 text-primary" />
      ) : (
        <ArrowDown className="h-3 w-3 text-primary" />
      )
    ) : (
      <ArrowUpDown className="h-3 w-3 opacity-30" />
    )

  /** Nach einer Änderung direkt in der Liste: neu laden — die Auswahl bleibt (`fetchData` hält sie). */
  const nachAenderung = () => void fetchData(filterValues, colState)

  const renderCell = (z: MaterialRow, col: ColDef): React.ReactNode => {
    switch (col.key) {
      case 'calculation':
        return (
          <Link to={`/calculations/${z.calculationId}?tab=positionen&pos=${z.positionId}`} className="text-primary hover:underline" title={z.calculationName}>
            {kalkText(z)} <span className="text-muted-foreground">{z.calculationName}</span>
          </Link>
        )
      case 'dueAt':
        return z.dueAt ? <span className={abgabeDringend(z.dueAt) ? 'font-semibold text-destructive' : undefined}>{datumText(z.dueAt, true)}</span> : '—'
      case 'position':
        return (
          <span>
            {z.positionLabel}
            {z.positionKind === 'OPTIONAL' && <span className="ml-1 rounded bg-muted px-1 text-xs text-muted-foreground">Eventual</span>}
            {z.positionKind === 'ALTERNATIVE' && <span className="ml-1 rounded bg-muted px-1 text-xs text-muted-foreground">Wahl</span>}
          </span>
        )
      case 'quantity':
        return mengeText(z.quantity)
      case 'unit':
        return (
          <>
            {z.unitCode ?? '—'}
            {z.altQuantity !== null && z.altUnitCode && (
              <span className="block text-xs text-muted-foreground" title="Alternativmenge">
                {mengeText(z.altQuantity)} {z.altUnitCode}
              </span>
            )}
          </>
        )
      case 'name':
        return (
          <div className="whitespace-normal">
            {z.name}
            {z.description && <div className="text-xs text-muted-foreground">{z.description}</div>}
          </div>
        )
      case 'articleNumber':
        return z.articleNumber ?? ''
      case 'supplier':
        if (darfAendern && z.writable) return <LieferantZelle z={z} onGeaendert={nachAenderung} />
        return z.supplierName ? (
          <span className={z.supplierIsSupplier ? undefined : 'text-destructive'} title={z.supplierIsSupplier ? undefined : 'nicht als Lieferant angelegt — wird nicht angefragt'}>
            {z.supplierName}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      case 'priceState':
        return z.hasPrice ? (
          <span className={z.priceOld ? 'text-muted-foreground' : undefined}>
            {PREISQUELLE[z.priceSource] ?? z.priceSource}
            {z.priceDate ? ` · ${tagText(z.priceDate)}` : ''}
          </span>
        ) : (
          <span className="text-muted-foreground">ohne Preis</span>
        )
      case 'unitCost':
        if (darfAendern && z.writable) return <EkZelle z={z} onGeaendert={nachAenderung} />
        return z.unitCost == null ? (
          '—'
        ) : (
          <span>
            {geldText(z.unitCost)}
            {z.priceUnit > 1 ? ` / ${z.priceUnit}` : ''}
            {z.discountPercent ? <span className="text-xs text-muted-foreground"> −{z.discountPercent} %</span> : null}
          </span>
        )
      case 'cost':
        return z.cost == null ? '' : geldText(z.cost)
      case 'requestState': {
        const s = ZUSTAND_ANZEIGE[z.requestState]
        const belege = z.requests.filter((a) => a.status !== 'CANCELLED')
        return (
          <div className="flex flex-wrap items-center gap-1">
            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${z.overdue ? 'bg-destructive/15 text-destructive' : s.klasse}`}>
              {z.overdue ? 'Frist überschritten' : s.label}
            </span>
            {z.requestState === 'ANSWERED' && z.offers > 0 && (
              <span className="text-xs text-muted-foreground">
                {z.offers} Angebot{z.offers === 1 ? '' : 'e'}
                {z.bestPrice != null && ` · bestes ${geldText(z.bestPrice)}${z.bestSupplierName ? ` (${z.bestSupplierName})` : ''}`}
              </span>
            )}
            {belege.map((a) => (
              <Link key={a.requestId} to={`/correspondence/${a.documentId}`} className="text-xs text-primary hover:underline" title={a.supplierName}>
                {a.documentNumber ?? 'Entwurf'}
              </Link>
            ))}
          </div>
        )
      }
      case 'files':
        return z.fileCount ? (
          <span className="inline-flex items-center gap-0.5" title="Dateien der Materialzeile — gehen mit der Anfrage hinaus">
            <Paperclip className="h-3.5 w-3.5" />
            {z.fileCount}
          </span>
        ) : (
          ''
        )
      case 'owner':
        return z.ownerName ?? ''
      case 'weight':
        return z.weight ? mengeText(Math.round(z.weight * 10) / 10) : ''
      case 'kind':
        return KOSTENART[z.kind] ?? z.kind
      case 'category':
        return z.categoryName ?? <span className="text-muted-foreground">—</span>
      default:
        return null
    }
  }

  const titelZeile = (g: Gruppe) => {
    const ids = g.rows.map((z) => z.costLineId)
    const alle = ids.every((id) => auswahl.has(id))
    const offen = !zu.has(g.key)
    const einheiten = new Set(g.rows.map((z) => z.unitCode))
    return (
      <tr key={`g-${g.key}`} className="border-b border-border bg-muted/40">
        <td className="px-2 py-1">
          <input type="checkbox" aria-label="alle Zeilen der Gruppe" checked={alle} onChange={(e) => schalte(ids, e.target.checked)} />
        </td>
        <td colSpan={totalCols - 1} className="px-2 py-1">
          <button
            type="button"
            className="inline-flex items-center gap-1 font-medium text-foreground"
            onClick={() =>
              setZu((alt) => {
                const neu = new Set(alt)
                if (neu.has(g.key)) neu.delete(g.key)
                else neu.add(g.key)
                return neu
              })
            }
          >
            {offen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {g.label}
          </button>
          <span className="ml-2 text-xs text-muted-foreground">
            {g.sub ? `${g.sub} · ` : ''}
            {g.rows.length} Zeile{g.rows.length === 1 ? '' : 'n'}
            {colState.grouping === 'material' && einheiten.size === 1 && ` · zusammen ${mengeText(g.rows.reduce((s, z) => s + z.quantity, 0))} ${g.rows[0].unitCode ?? ''}`}
          </span>
        </td>
      </tr>
    )
  }

  // Nachtrag 7 (N7-10): was allein der Filter „Anfrage“ ausblendet (schon angefragt, übernommen, nicht anfragen —
  // je nach Wahl) — die Liste nennt es und holt es mit einem Klick herein.
  const alleZustaende = Object.keys(ZUSTAND_ANZEIGE) as AnfrageZustand[]
  const verborgen = alleZustaende.map((s) => [s, result?.hiddenByRequestState?.[s] ?? 0] as const).filter(([, n]) => n > 0)
  const verborgenSumme = verborgen.reduce((s, [, n]) => s + n, 0)
  const zeigeVerborgene = () => {
    const gewaehlt = (filterValues.requestState?.value ?? '').split(',')
    const neu: Record<string, ActiveFilter> = {
      ...filterValues,
      requestState: { fieldKey: 'requestState', operator: 'is', value: alleZustaende.filter((s) => gewaehlt.includes(s) || verborgen.some(([v]) => v === s)).join(',') },
    }
    setFilterValues(neu)
    void fetchData(neu, colState)
  }

  return (
    <div className="space-y-4">
      <FilterBar
        availableFields={filterFields}
        value={filterValues}
        onChange={handleFiltersChange}
        layoutKey="calculation_material"
        defaultFieldKeys={DEFAULT_FILTER_KEYS}
        defaultValues={DEFAULT_FILTER_VALUES}
      />
      {verborgenSumme > 0 && (
        <p className="text-xs text-muted-foreground" data-verborgen>
          {verborgenSumme} weitere {verborgenSumme === 1 ? 'Zeile' : 'Zeilen'} blendet der Filter „Anfrage“ aus: {verborgen.map(([s, n]) => `${n} ${ZUSTAND_ANZEIGE[s].label}`).join(' · ')} —{' '}
          <button type="button" className="text-primary hover:underline" onClick={zeigeVerborgene}>
            einblenden
          </button>
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <label className="text-muted-foreground" htmlFor="material-gruppe">
            Gruppieren nach
          </label>
          <select
            id="material-gruppe"
            className="rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
            value={colState.grouping}
            onChange={(e) => updateColState((prev) => ({ ...prev, grouping: e.target.value as Gruppierung }))}
          >
            <option value="supplier">Lieferant</option>
            <option value="calculation">Kalkulation</option>
            <option value="material">Material</option>
            <option value="none">ohne Gruppierung</option>
          </select>
          {gewaehlt.length > 0 && (
            <>
              <span className="ml-2 text-muted-foreground">{gewaehlt.length} gewählt</span>
              {onAnfragen && result?.canCreateRequests && (
                <button type="button" className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-primary-foreground" onClick={() => onAnfragen(gewaehlt)}>
                  <Send className="h-4 w-4" /> Preisanfrage erzeugen …
                </button>
              )}
              <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 hover:bg-muted" onClick={() => void aktion('skip')}>
                <Ban className="h-4 w-4" /> Nicht anfragen
              </button>
              <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 hover:bg-muted" onClick={() => void aktion('unskip')}>
                <RotateCcw className="h-4 w-4" /> Wieder anfragen
              </button>
              <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 hover:bg-muted" onClick={() => void aktion('copy')}>
                <Copy className="h-4 w-4" /> Kopieren
              </button>
            </>
          )}
        </div>
        <ExportButton
          request={(format, orientation) =>
            materialExportRequest(
              { ...filterParams(filterValues, { calculationId }), ...(colState.sortKey ? { sortBy: colState.sortKey, sortDir: colState.sortDir } : {}), format, orientation },
              shownColumns.map((c) => ({ key: c.key, label: c.label })),
            )
          }
          mail={{ source: 'EXPORT_CALCULATION_MATERIAL', title: t('mailExport.titles.EXPORT_CALCULATION_MATERIAL'), subject: 'Materialanfragen' }}
        />
      </div>

      {result && !result.canCreateRequests && (
        <div className="rounded-md bg-muted px-4 py-2 text-sm text-muted-foreground">
          Preisanfragen brauchen das Belegwesen und das Recht, Preisanfragen anzulegen — Kopieren, Export und „nicht anfragen" gehen trotzdem.
        </div>
      )}
      {hinweis && <div className="rounded-md bg-primary/10 px-4 py-2 text-sm text-foreground">{hinweis}</div>}
      {result?.meta.truncated && (
        <div className="rounded-md bg-amber-100 px-4 py-2 text-sm text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
          Es sind mehr als 2.000 Zeilen — bitte mit den Filtern eingrenzen.
        </div>
      )}

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
            columnConfigMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
          title="Spalten konfigurieren"
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={LAYOUT_KEY}
              currentValue={colState}
              onReset={(value) => {
                const next = { ...VORGABE, ...((value as Partial<ColumnState> | null) ?? {}) }
                markUiSettingApplied(LAYOUT_KEY, next)
                setColState(next)
                void fetchData(filterValues, next)
              }}
            />
          </div>
        )}

        {error && <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="w-8 px-2 py-1.5">
                  <input
                    type="checkbox"
                    aria-label="alle Zeilen"
                    checked={rows.length > 0 && rows.every((z) => auswahl.has(z.costLineId))}
                    onChange={(e) => schalte(rows.map((z) => z.costLineId), e.target.checked)}
                  />
                </th>
                {shownColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`relative select-none px-3 py-1.5 font-medium text-foreground ${col.align === 'right' ? 'text-right' : 'text-left'} ${
                      columnConfigMode ? 'cursor-grab' : ''
                    } ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => (dragKeyRef.current = col.key) : undefined}
                    onDragOver={
                      columnConfigMode
                        ? (e) => {
                            e.preventDefault()
                            setDragOverKey(col.key)
                          }
                        : undefined
                    }
                    onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                    onDragEnd={columnConfigMode ? () => setDragOverKey(null) : undefined}
                    style={colState.columnWidths[col.key] ? { width: colState.columnWidths[col.key] } : undefined}
                  >
                    <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                      <button type="button" onClick={() => handleSort(col.key)} className="flex items-center gap-1 hover:text-primary">
                        {col.label} {pfeil(col.key)}
                      </button>
                      {columnConfigMode && (
                        <button
                          type="button"
                          onClick={() => updateColState((prev) => ({ ...prev, columnKeys: prev.columnKeys.filter((k) => k !== col.key) }))}
                          className="ml-1 text-muted-foreground hover:text-destructive"
                          title="Spalte entfernen"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40" onMouseDown={(e) => handleResizeStart(e, col.key)} />
                  </th>
                ))}
                {columnConfigMode && availableToAdd.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const key = e.target.value
                        if (!key) return
                        updateColState((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">+ Spalte</option>
                      {availableToAdd.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </th>
                )}
                <th className="w-4" />
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Laden…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Kein Material gefunden — die Filter oben bestimmen, welche Zeilen erscheinen.
                  </td>
                </tr>
              )}
              {gruppen.map((g) => [
                ...(colState.grouping === 'none' ? [] : [titelZeile(g)]),
                ...(zu.has(g.key)
                  ? []
                  : g.rows.map((z, idx) => (
                      <tr
                        key={z.costLineId}
                        className={`border-b border-border last:border-0 ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'} ${
                          auswahl.has(z.costLineId) ? 'bg-primary/5' : ''
                        } transition-colors hover:bg-accent/30`}
                      >
                        <td className="px-2 py-1.5">
                          <input type="checkbox" aria-label={z.name} checked={auswahl.has(z.costLineId)} onChange={(e) => schalte([z.costLineId], e.target.checked)} />
                        </td>
                        {shownColumns.map((col) => (
                          <td key={col.key} className={`px-3 py-1.5 ${col.key === 'name' ? '' : 'whitespace-nowrap'} ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                            {renderCell(z, col)}
                          </td>
                        ))}
                        <td />
                      </tr>
                    ))),
              ])}
              {rows.length > 0 && result && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td className="px-2 py-1.5" />
                  {shownColumns.map((col, i) => (
                    <td key={col.key} className={`whitespace-nowrap px-3 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                      {i === 0
                        ? `${result.totals.count} Zeilen`
                        : col.key === 'weight'
                          ? `${mengeText(Math.round(result.totals.weight * 10) / 10)} kg`
                          : col.key === 'cost' && result.totals.cost != null
                            ? geldText(result.totals.cost)
                            : ''}
                    </td>
                  ))}
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
